/**
 * The pure presentation helper behind the **Build Order** section —
 * 05-project-hub-build-order.
 *
 * It contains NO ordering logic of its own and reuses the single source of
 * order in `buildOrderTree.ts`: per-sibling-group `build_order` (epics number
 * 1, 2, 3… among top-level siblings; each parent's children number
 * independently from 1), composed into the project's overall order by the DFS
 * tree walk. Blockedness is judged exactly as `readyToStartItems()` does — a
 * blocker is unmet iff it is among the items passed in (an absent /
 * already-done blocker is satisfied and does not block) — and annotated INLINE
 * per row (`blockedByNames`), never as a separate partition.
 *
 * Pure (no React, no IO): unit-testable in isolation; the section is a thin
 * render over the {@link ProjectBuildOrderTreeView} output.
 */

import type { WorkItemRow, WorkItemDependencyRow } from '../../components/projects/types';
import {
  nextUpId,
  siblingGroups,
  treeWalkOrder,
  unmetBlockers,
  type BuildOrderTreeItem
} from './buildOrderTree';

/** The minimal dependency-edge shape: the blocked item and its blocker. */
export type BuildOrderDependency = Pick<
  WorkItemDependencyRow,
  'work_item_id' | 'depends_on_work_item_id'
>;

/**
 * The minimal work-item shape the tree view reads: the tree-module fields
 * ({@link BuildOrderTreeItem}) plus the display name and completed flag.
 */
export type BuildOrderTreeViewItem = BuildOrderTreeItem &
  Pick<WorkItemRow, 'name'> & { isCompleted?: boolean };

/** One rendered row in the epic-grouped Build Order tree. */
export interface BuildOrderTreeRow {
  id: string;
  name: string;
  build_order: number | null;
  /** 1-based position within its SORTED sibling group (a display index, not the raw value). */
  position: number;
  /** Display names of still-pending blocker(s) — inline "Blocked by X"; empty = ready. */
  blockedByNames: string[];
  /** True on exactly the one row `nextUpId()` names (the actionable next step). */
  nextUp: boolean;
  /** This row's children, sorted — an epic's items, or a story/task's subtasks. */
  children: BuildOrderTreeRow[];
  /** Epic rows render as expandable cards; everything else renders as a row. */
  kind: 'epic' | 'item';
  parentId: string | null;
  isCompleted: boolean;
}

export interface ProjectBuildOrderTreeView {
  /** Top-level sibling rows in order: epic cards interleaved with orphan items. */
  topLevel: BuildOrderTreeRow[];
  /** The DFS tree-walk id order — the agent-moved-cue diff runs over this. */
  flatWalkIds: string[];
  /** The single actionable next item ({@link nextUpId}), or null. */
  nextUpId: string | null;
}

/**
 * Shape the open project's work items into the epic-grouped Build Order tree:
 * top-level siblings (epic cards + orphan items) in per-sibling build order,
 * each parent's children nested beneath it with positions restarting at 1, and
 * blockedness annotated inline per row. ALL ordering, readiness, and next-up
 * logic delegates to `buildOrderTree.ts` (the single source of order) — this
 * helper only resolves display names and shapes rows. Degenerate input returns
 * a well-formed empty view and never throws.
 */
export function projectBuildOrderTreeView(
  workItems: ReadonlyArray<BuildOrderTreeViewItem>,
  dependencies: ReadonlyArray<BuildOrderDependency>
): ProjectBuildOrderTreeView {
  const items = (workItems ?? []).filter(
    (w): w is BuildOrderTreeViewItem => !!w && typeof w.id === 'string'
  );
  const deps = dependencies ?? [];

  const nameById = new Map(items.map((w) => [w.id, w.name]));
  const groups = siblingGroups(items);
  const blockers = unmetBlockers(items, deps);
  const next = nextUpId(items, deps);

  const buildRows = (
    parentId: string | null,
    visited: Set<string>
  ): BuildOrderTreeRow[] => {
    const group = groups.get(parentId) ?? [];
    const rows: BuildOrderTreeRow[] = [];
    for (const w of group) {
      if (visited.has(w.id)) continue; // cycle guard — mirrors treeWalkOrder
      visited.add(w.id);
      rows.push({
        id: w.id,
        name: w.name,
        build_order: w.build_order,
        position: rows.length + 1,
        blockedByNames: (blockers.get(w.id) ?? []).map((id) => nameById.get(id) ?? ''),
        nextUp: w.id === next,
        children: buildRows(w.id, visited),
        kind: w.type === 'epic' ? 'epic' : 'item',
        parentId,
        isCompleted: w.isCompleted === true
      });
    }
    return rows;
  };

  return {
    topLevel: buildRows(null, new Set<string>()),
    flatWalkIds: treeWalkOrder(items).map((w) => w.id),
    nextUpId: next
  };
}
